'use client';
import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Grid, Typography, useMediaQuery } from '@mui/material';
import Image from 'next/image';
import { nextImageTransitionProps } from '@/constants/next-image-transition-props';
import { config } from '@/config/environment';
import DesktopView from './components/desktop-view';
import MobileView from './components/mobile-view';
import { sanityClient } from '@/content/sanity/sanity-client/sanity-client';
import { TheoryOfAction } from '@/types/theories';

const MainDiv = styled.div`
  height: auto;
  margin: 0;
  display: flex;
  overflow: hidden;
  flex-direction: column;
  justify-content: space-between;
  background: #231727;
  @media (max-width: 768px) {
    padding: 98px 0 0 0;
    width: 100%;
  }
`;
const Div = styled(Grid)`
  width: 100%;
  height: auto;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding: 0 12.5%;
  @media (max-width: 768px) {
    padding: 0 20px;
    width: 100%;
  }
`;
const TitleDiv = styled(Grid)`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: flex-start;
  padding: 18.33% 0 15% 0;
  height: auto;
  z-index: 1;
  margin-bottom: 32px;
  @media (max-width: 768px) {
    padding: 0;
  }
`;

const Title = styled(Typography)`
  font-family: var(--font-cooper-light), sans-serif;
  font-size: 50px;
  line-height: 109.3%;
  color: #fdf3de;
  text-align: start;
  margin: 0;
  @media (max-width: 768px) {
    text-align: start;
    color: #ffffff;
    width: 80%;
  }
`;
const Text = styled(Typography)`
  font-family: var(--font-work-sans), sans-serif;
  font-size: 18px;
  line-height: 159.3%;
  color: #ffffff;
  text-align: start;
  margin-left: 20px;
  @media (max-width: 768px) {
    width: 100%;
    margin-left: 0;
  }
`;
const Vector = styled.img`
  object-fit: cover;
  position: absolute;
  margin-top: 60px;
  width: 47.4%;
  left: 0;
`;
const VideoDiv = styled(Grid)`
  margin-top: 50px;
  display: flex;
  flex-direction: row;
  justify-content: center;
  @media (max-width: 768px) {
    height: auto;
    width: 100%;
  }
`;
const Video = styled.div`
  height: auto;
  width: 600px;
  z-index: 20;
  position: relative;
  @media (max-width: 768px) {
    height: auto;
    width: 100%;
  }
`;
const VideoImageBlob = styled(Image)`
  display: block;
  object-fit: cover;
  height: auto;
  position: absolute;
  width: 80%;
  right: 0;
  margin-top: 50px;
  z-index: 2;
`;
VideoImageBlob.defaultProps = {
  ...nextImageTransitionProps,
  style: undefined,
};

const TheoriesDiv = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: auto;
  @media (max-width: 768px) {
    margin-top: -250px;
    background: #ffffff;
  }
`;
const Wave = styled.div`
  display: inline-block;
  position: relative;
  width: 100%;
  height: 200px;
  vertical-align: middle;
  z-index: 1;
  overflow: visible;
  transform: scaleX(-1);
  svg {
    display: inline-block;
    position: absolute;
    top: -1px;
    left: 0;
    padding-left: -20px;
  }
`;

const TheoryOfChange = () => {
  const isMobile = useMediaQuery('(max-width:960px)');
  const archiveMode = config.platformMode === 'archive';
  const [theoryOfAction, setTheoryOfAction] = useState<TheoryOfAction | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTheoryOfAction = async () => {
      const data = await sanityClient.fetch(
        `*[_type=="theoryOfAction"][0]{ title, description, theories[]{ name, detail, image } }`,
      );
      setTheoryOfAction(data);
      setLoading(false);
    };

    fetchTheoryOfAction();
  }, []);

  if (loading || !theoryOfAction) {
    return <MainDiv></MainDiv>;
  }

  const archiveNames = ['Individuals & families', 'Organizations & funders', 'Community & culture'];
  const archiveDetails = [
    "Drawing on what's called \"Ecological Systems Theory,\" we've looked at individuals and families as part of a broader context. What we do, say, think and feel is a product of what's inside of us and what's around us. So Curiko operated inside-out, and outside-in. We offered opportunities for people to see themselves in new ways, set their own intentions, make their own choices, grow their motivation, forge relationships, and experience more moments of critical awareness and authentic connection. This was through one-on-one and group experiences plus personalized coaching. We also worked to reset expectations, norms, and reference points. This was by creating paid leadership roles for people with disabilities, encouraging peer-to-peer modelling, and telling stories about what's possible.",
    'Organizations and funders are also shaped by the broader context. Since institutions closed, there has been a big focus on keeping people with developmental disabilities protected, safe, and occupied. There has not ben enough attention on learning, growth, and actualization. Curiko challenged entrenched institutional thinking. We offered opportunities for staff to reimagine their roles (and the role of community), critically explore power dynamics, reframe risk, and try out two-way relationships. This was done through building capacity for staff to host experiences on the platform, modeling new kinds of practice, and running organizational learning cohorts.',
    "Creating communities where we all belong and flourish isn't the sole responsibility of individuals and families with disabilities, or social service organizations and funders. It is a collective responsibility. Very few people regularly interact with people with disabilities, or have a chance to reflect on structural inequalities, ableist stereotypes, and personal discomforts. Curiko created a space for living into horizontal relationships. We offered opportunities for anyone and everyone to contribute, learn about disability justice, and explore limiting beliefs. We did this by recruiting and supporting people in various communities to turn their passions into experiences on the platfoms and spreading new kinds of narratives through public events.",
  ];

  const theoriesToRender = archiveMode
    ? (theoryOfAction?.theories ?? []).map((theory, index) => ({
        ...theory,
        name: archiveNames[index] ?? theory.name,
        detail: archiveDetails[index] ?? theory.detail,
      }))
    : theoryOfAction?.theories ?? [];

  return (
    <MainDiv>
      <Div container>
        {!isMobile && <Vector src="/about-us/toc/vector2.png" alt={'vector'} />}
        <TitleDiv item md={6} xs={12}>
          <Title>{archiveMode ? "Curiko's Theory of Action" : theoryOfAction?.title}</Title>
        </TitleDiv>
        <TitleDiv item md={6} xs={12}>
          <Text>
            {archiveMode
              ? 'We always saw Curiko as more than a program, and Curiko experiences as more than activities. Curiko was a systemic intervention designed to bridge people across lines of difference, nurture two-way relationships, challenge social norms, and demonstrate an alternative to segregated disability services. Our work has spanned three interconnected levels: (1) individuals and families, (2) organizations and funders, (3) community and culture. '
              : theoryOfAction?.description}
          </Text>
        </TitleDiv>
      </Div>
      <Div>
        <VideoDiv container>
          <Video>
            <Image {...nextImageTransitionProps} src={'/about-us/toc/main-image.png'} alt="" />
          </Video>
          {isMobile && <VideoImageBlob src="/about-us/toc/mobile-blob.png" alt="" />}
        </VideoDiv>
      </Div>
      {isMobile && (
        <Wave>
          <svg viewBox="0 0 498 200" fill="#231727" preserveAspectRatio="xMinYMin meet">
            <path
              d="M0,100 C150,200 350,0 500,100 L500,00 L0,0 Z"
              style={{ stroke: 'none', fill: '#231727' }}
            ></path>
          </svg>
        </Wave>
      )}
      <TheoriesDiv>
        {!isMobile ? (
          <DesktopView theories={theoriesToRender} archiveMode={archiveMode} />
        ) : (
          <MobileView theories={theoriesToRender} />
        )}
      </TheoriesDiv>
    </MainDiv>
  );
};

export default TheoryOfChange;
